import { useAnalyticContext } from '@application/analytic/analytic_link/mod';
import { ActionCard, BadgeText, BreakPoints, Icon, IconDictionary, useBreakpoint } from '@cellula/react';
import { ErrorBoundary } from '@component/error_boundary.component';
import { ExternalSvg } from '@component/external_svg/external_svg.component';
import { ChannelKind } from '@visiba/api-vcs-client-v2';
import { Fragment } from 'react';
import { FlexibleButtonIconModel } from '../models/flexible_button_icon';
import { Routeable } from './_flexible_identifier_routeable.component';

interface Props {
	component: FlexibleButtonIconModel;
	accessFlow?: {
		isOpen?: boolean;
		text?: string;
	};
}
function getIcon(icon: NonNullable<FlexibleButtonIconModel['icon']>): JSX.Element {
	switch (icon.type) {
		case 'local':
			return (
				<ErrorBoundary fallback={<Fragment />}>
					<Icon size='small' type={icon.origin as keyof IconDictionary} />
				</ErrorBoundary>
			);
		case 'svgUrl':
			return <ExternalSvg imgSrc={icon.origin} size={32} />;
		case 'pngUrl':
			return <img src={icon.origin} height={32} alt='' />;
		default: {
			const shouldNeverHappen: never = icon.type;

			return shouldNeverHappen;
		}
	}
}

export function FlexibleActionCard({ component, accessFlow, ...rest }: Props): JSX.Element {
	const iconElement = component.icon != null ? getIcon(component.icon) : undefined;
	const breakpoint = useBreakpoint();
	const analyticCtx = useAnalyticContext();
	const isMessaging = component.channel === ChannelKind.Messaging;

	function analyticQuirk_handleButtonClick(): void {
		// If the url was not parsed through "hostnameIsWithinSameDomain"
		// (within flexible_component.service), it'll not be an absolute.
		// And if it's not absolute it should count as an external button.
		if (component.openUrlWith !== 'SameTab' || component.url.includes('http')) {
			analyticCtx.trackEvent(`Clicked: External button`);

			return;
		}

		analyticCtx.trackEvent(`Clicked: button ${isMessaging ? 'messaging' : 'unknown'}`);
	}

	return (
		<Routeable
			url={component.url}
			requiresAuthentication={component.requiresAuthentication}
			openWith={component.openUrlWith}
			sourceButtonId={component.id}
			{...rest}
		>
			<ActionCard.Root
				data-testid='FlexibleActionCard'
				variant={breakpoint === BreakPoints.Small ? 'default' : 'chunky'}
				icon={iconElement}
				as='span'
				onClick={analyticQuirk_handleButtonClick}
			>
				<ActionCard.Title>{component.title}</ActionCard.Title>
				<ActionCard.Meta>{component.subtitle}</ActionCard.Meta>
				{isMessaging && accessFlow && (
					<div style={{ position: 'absolute', bottom: '10px' }}>
						<BadgeText size='medium' variant='info'>
							{accessFlow.text ?? ''}
						</BadgeText>
					</div>
				)}
			</ActionCard.Root>
		</Routeable>
	);
}
